import { API } from "../../lib/api";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Loader2, Film, Image as ImageIcon, Trash2, Scissors, Check } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";

export const fileUrl = (p) => (p ? `${API}/files/${p}` : null);

export function EditorialSource({ clip }) {
  const { t } = useTranslation();
  if (!["specific", "related", "general"].includes(clip.editorial_level)) return null;
  return <div className="text-xs text-slate-500" title={t(`clip.editorial_${clip.editorial_level}_hint`)}>
    {t(`clip.editorial_${clip.editorial_level}`)}
  </div>;
}

export function YouTubeSource({ clip }) {
  const { t } = useTranslation();
  if (!/^https:\/\/(?:[a-z0-9-]+\.)*(?:youtube\.com|youtu\.be)\//i.test(clip.source_url || "")) return null;
  const url = new URL(clip.source_url);
  const interval = Number.isFinite(clip.source_start) && Number.isFinite(clip.source_end);
  if (interval) url.searchParams.set("t", `${Math.floor(clip.source_start)}s`);
  return <div className="text-xs">
    <a href={url.toString()} target="_blank" rel="noopener noreferrer" className="block text-blue-600 underline truncate">
      {clip.author || "YouTube"}
    </a>
    {interval && <div className="text-slate-500">{t("clip.sourceInterval", {
      start: clip.source_start.toFixed(1), end: clip.source_end.toFixed(1),
    })}</div>}
  </div>;
}

export const toClip = (v) => ({
  id: crypto.randomUUID(),
  title: v.title,
  duration: v.duration,
  source: v.source || v.origin || "Subido",
  media: v.media || "video",
  video_url: fileUrl(v.video_path),
  thumbnail: v.thumbnail_url || fileUrl(v.thumb_path),
  start: v.start,
  end: v.end,
  license: v.license,
  attribution_required: v.attribution_required,
  author: v.author,
  source_url: v.source_url,
  channel_id: v.channel_id,
  video_id: v.video_id,
  source_start: v.source_start,
  source_end: v.source_end,
  source_duration: v.source_duration,
  subject: v.subject,
  identity_verified: v.identity_verified,
  identity_verification: v.identity_verification,
  verified_frame_times: v.verified_frame_times,
  needs_review: v.needs_review,
  needs_review_reason: v.needs_review_reason,
  // Texto con el que se encontró + fuente canónica (pexels/unsplash/wikimedia/...) — el panel
  // de revisión de material (OneClickProducer) los usa para el badge/tooltip de procedencia.
  // Solo vienen informados si el doc pasó por /broll/library/save con "query" (ver PexelsBrowser)
  // o por el auto-relleno del director de cine (find_best_media).
  director_query: v.director_query || "",
  director_source: v.director_source || "",
  editorial_level: v.editorial_level,
  editorial_query: v.editorial_query,
  visual_request: v.visual_request,
});

export function ClipCard({
  clip, primaryLabel, primaryIcon: PIcon = Plus, primaryTestId, onPrimary, primaryBusy,
  secondaryLabel, secondaryIcon: SIcon = Plus, secondaryTestId, onSecondary, secondaryBusy,
  canCut, onCut, cutting, cutProgress, onDelete, deleteTestId, displayOnly, compact,
  selectable, selected, onToggleSelect,
}) {
  const { t } = useTranslation();
  const isImage = clip.media === "image";
  const isVideo = !isImage && !!clip.video_url;
  const videoRef = useRef(null);
  const [thumbError, setThumbError] = useState(false);

  const startPreview = () => {
    const v = videoRef.current;
    if (!v) return;
    try {
      if (clip.start != null) v.currentTime = clip.start;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) { /* ignore */ }
  };
  const stopPreview = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    try { v.currentTime = clip.start != null ? clip.start : 0; } catch (e) { /* ignore */ }
  };
  const onTime = () => {
    const v = videoRef.current;
    if (!v) return;
    if (clip.end != null && v.currentTime >= clip.end) {
      v.currentTime = clip.start != null ? clip.start : 0;  // bucle dentro del corte
    }
  };

  return (
    <div className={`border-2 rounded-lg overflow-hidden group bg-white transition-colors ${selectable && selected ? "border-blue-600 ring-2 ring-blue-600/25" : "border-slate-200"}`}>
      <div
        className={`relative w-full ${compact ? "h-16" : "h-28"} bg-slate-200 ${selectable ? "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-offset-2" : ""}`}
        onMouseEnter={isVideo ? startPreview : undefined}
        onMouseLeave={isVideo ? stopPreview : undefined}
        onClick={selectable ? onToggleSelect : undefined}
        role={selectable ? "checkbox" : undefined}
        aria-checked={selectable ? selected : undefined}
        aria-label={selectable ? t(selected ? "clip.deselect" : "clip.select", { title: clip.title }) : undefined}
        tabIndex={selectable ? 0 : undefined}
        onKeyDown={selectable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleSelect(); } } : undefined}
      >
        {clip.thumbnail && !thumbError
          ? <img src={clip.thumbnail} alt={clip.title} loading="lazy" decoding="async" className="w-full h-full object-cover" onError={() => setThumbError(true)} />
          : <div className="w-full h-full flex items-center justify-center bg-slate-800">
              {isImage ? <ImageIcon size={compact ? 20 : 26} className="text-slate-400" /> : <Film size={compact ? 20 : 26} className="text-slate-400" />}
            </div>}
        {selectable && (
          <span
            data-testid={`select-clip-${clip.id}`}
            aria-hidden="true"
            className={`absolute top-1 left-1 z-20 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${selected ? "bg-blue-600 border-blue-600" : "bg-white/80 border-white group-hover:border-slate-300"}`}
          >
            {selected && <Check size={16} strokeWidth={3} className="text-white" />}
          </span>
        )}
        {selectable && <div className={`absolute inset-0 bg-blue-600/10 transition-opacity ${selected ? "opacity-100" : "opacity-0"}`} />}
        {isVideo && (
          <video
            ref={videoRef}
            src={clip.video_url}
            data-testid={`video-preview-${clip.id}`}
            muted playsInline preload="none" loop={clip.end == null}
            onLoadedData={() => { const v = videoRef.current; if (v && clip.start != null) { try { v.currentTime = clip.start; } catch (e) {} } }}
            onTimeUpdate={clip.end != null ? onTime : undefined}
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
          />
        )}
        <span className="absolute bottom-1 right-1 z-10 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
          {isImage ? <ImageIcon size={10} /> : <Film size={10} />} {clip.duration}s
        </span>
        {clip.source && <span className="absolute bottom-1 left-1 z-10 bg-slate-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{clip.source.toUpperCase()}</span>}
        {clip.attribution_required && (
          <span
            className={`absolute top-1 z-10 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded max-w-[70%] truncate ${onDelete ? "right-8" : "right-1"}`}
            title={clip.license ? `${t("pex.attributionRequired")}: ${clip.license}${clip.author ? " · " + clip.author : ""}` : t("pex.attributionRequired")}
          >
            © {clip.license || t("pex.attributionRequired")}
          </span>
        )}
        {onDelete && (
          <button data-testid={deleteTestId} onClick={onDelete} className="absolute top-1 right-1 z-20 bg-white/90 text-slate-600 hover:text-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
        )}
      </div>
      <div className="p-2">
        <div className="text-sm font-semibold text-slate-800 truncate" title={clip.title}>{clip.title}</div>
        <YouTubeSource clip={clip} />
        <EditorialSource clip={clip} />
        {!displayOnly && (
          <>
            <button data-testid={primaryTestId} onClick={onPrimary} disabled={primaryBusy} className="mt-2 w-full bg-blue-600 text-white hover:bg-blue-700 rounded-md py-2 text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60">
              {primaryBusy ? <Loader2 className="animate-spin" size={14} /> : <PIcon size={15} />} {primaryLabel}
            </button>
            {onSecondary && (
              <button data-testid={secondaryTestId} onClick={onSecondary} disabled={secondaryBusy} className="mt-1.5 w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-md py-2 text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60">
                {secondaryBusy ? <Loader2 className="animate-spin" size={14} /> : <SIcon size={15} />} {secondaryLabel}
              </button>
            )}
            {canCut && (
              <div className="mt-1.5">
                <TooltipProvider delayDuration={120}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button data-testid={`cut-clip-${clip.id}`} onClick={onCut} disabled={cutting} className="w-full bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-md py-1.5 text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-80">
                        {cutting
                          ? <><Loader2 className="animate-spin" size={14} /> {cutProgress?.phase ? t(`clip.cutPhase_${cutProgress.phase}`) : t("clip.cutting")} {cutProgress?.pct != null ? `${cutProgress.pct}%` : ""}{cutProgress?.total ? ` · ${cutProgress.done}/${cutProgress.total}` : ""}</>
                          : <><Scissors size={14} /> {t("clip.cut7s")}</>}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px] bg-slate-900 text-white text-sm font-medium leading-snug px-3 py-2 rounded-xl shadow-2xl border border-white/10">
                      {t("clip.cutTooltip")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {cutting && (
                  <div data-testid={`cut-progress-${clip.id}`} className="mt-1 h-1.5 w-full bg-amber-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all duration-500" style={{ width: `${cutProgress?.pct || 3}%` }} />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
